/* eslint-disable react-hooks/rules-of-hooks */
/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from 'react-router-dom';
import { Form, Formik, Field } from 'formik';
import * as yup from 'yup';

import Layout from "components/Layout/index";
import SubTitle from "components/SubTitle";
import ContentCard from "components/ContentCard";
import Button, { ButtonContainer } from 'components/Button';
import BrokingSummary from "components/BrokingSummary";
import TextField from 'components/Form/TextField';
import { InputField } from "components/Form";
import Loader from 'components/Loader';
import Colors from 'constants/Colors';
import { Col, Row } from 'components/Grid';

import TagList2 from 'components/TagList2';
import { GlobalLoaderContext } from 'store/context';
import { GlobalAuthContext } from 'store/context';
import { FitinsurAPI, MessageCenter } from 'utils';
import { LostCancelButton, TextEditorBox } from 'components/index';
import StepNavigation from "components/StepNavigation/index";
import { ButtonContent, CalendarContainer, Title, BorderSwitch } from "./styles";
import { DayPicker } from 'react-day-picker';
import DragZoneUpload from 'components/DragZoneUpload';
import Label from "components/Form/Label";
import Switch from 'react-switch';
import { useHeaderNbi, useScheduleReport } from 'services/QuoteService';
import { useOperationInfo } from 'services/OperationService';
import { cloneDeep, forEach } from 'lodash';
import { onValidationError } from '../../../../utils/src/validationForm';
import { FormSectionGlobal as FormSection } from '@fitinsur/components';
import {
  useGetQuestionnaireAnswer,
  useSaveQuestionnaireAnswer
} from '@fitinsur/services/QuestionnaireAnswerService';
import {
  useUpdateOperations
} from '@fitinsur/services/OperationService';
import PremiumCalculationSection from "components/PremiumCalculationSection/index";
import HasPermission from "components/HasPermission/index";

const SECTIONS = {
  ENDORSEMENT_EDIT: { name: 'ENDORSEMENT_EDIT', isMultiple: false, isRisk: false }
};

export default function EndorsementDocumentEdit(props) {
  const [summary, setSummary] = useState({});
  const authContext = useContext(GlobalAuthContext);
  const [endorsmentType, setEndorsmentType] = useState("");
  const [isApproved, setIsApproved] = useState(false);
  const [showCalendarEffective, setShowCalendarEffective] = useState(false);
  const [showCalendarExpire, setShowCalendarExpire] = useState(false);
  const [idDomainDocument, setIdDomainDocument] = useState(null);
  const [idDomainStatusDocument, setIdDomainStatusDocument] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [idDocumentsToSave, setIdDocumentsToSave] = useState([]);
  const [insurersToGenerateMrc, setInsurersToGenerateMrc] = useState([]);
  const [willGenerateMrcDocument, setWillGenerateMrcDocument] = useState(false);
  const [idProduct, setIdProduct] = useState(null);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [operationMetadata, setOperationMetadata] = useState(null);
  const [isDisable, setIsDisable] = useState(true);
  const [generateSignatureEndorsementsLoading, setGenerateSignatureEndorsementsLoading] = useState(false);
  const [operation, setOperation] = useState(null)

  const formik = useRef(null);
  const history = useHistory();
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);

  const {
    data: useHeaderNbiData,
    loading: useHeaderNbiLoading,
    error: useHeaderNbiError,
    status: useHeaderNbiStatus,
    request: useHeaderNbiRequest,
  } = useHeaderNbi();

  const {
    data: useOperationInfoData,
    loading: useOperationInfoLoading,
    error: useOperationInfoError,
    status: useOperationInfoStatus,
    request: useOperationInfoRequest,
  } = useOperationInfo(props.match.params.idOperation);

  const {
    data: useGetQuestionnairesAnswersData,
    loading: useGetQuestionnairesAnswersLoading,
    error: useGetQuestionnairesAnswersError,
    status: useGetQuestionnairesAnswersStatus,
    request: useGetQuestionnairesAnswersRequest,
  } = useGetQuestionnaireAnswer(props.match.params.idOperation);

  const {
    data: useSaveQuestionnaireAnswerData,
    loading: useSaveQuestionnaireAnswerLoading,
    error: useSaveQuestionnaireAnswerError,
    status: useSaveQuestionnaireAnswerStatus,
    request: useSaveQuestionnaireAnswerRequest,
  } = useSaveQuestionnaireAnswer(props.match.params.idOperation);

  const {
    data: useUpdateOperationsData,
    loading: useUpdateOperationsLoading,
    error: useUpdateOperationsError,
    status: useUpdateOperationsStatus,
    request: useUpdateOperationsRequest,
  } = useUpdateOperations(props.match.params.idOperation);

  const {
    data: useScheduleReportData,
    loading: useScheduleReportLoading,
    error: useScheduleReportError,
    status: useScheduleReportStatus,
    request: useScheduleReportRequest,
  } = useScheduleReport(props.match.params.idOperation);

  useEffect(() => {
    if (
      useHeaderNbiLoading ||
      useOperationInfoLoading ||
      useGetQuestionnairesAnswersLoading ||
      useSaveQuestionnaireAnswerLoading ||
      useUpdateOperationsLoading ||
      useScheduleReportLoading ||
      generateSignatureEndorsementsLoading
    ) {
      loading.start();
    } else {
      loading.stop();
    }
  }, [
    useHeaderNbiLoading,
    useOperationInfoLoading,
    useGetQuestionnairesAnswersLoading,
    useSaveQuestionnaireAnswerLoading,
    useUpdateOperationsLoading,
    useScheduleReportLoading,
    generateSignatureEndorsementsLoading,
  ]);

  useEffect(() => {
    buildValidation();

    const contractDocuments = questionnaire.find((qst) => qst.idQuestion === 1867)?.answer || [];

    if (contractDocuments.length > 0 && contractDocuments !== '[]') {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  }, [questionnaire]);

  useEffect(() => {
    if (useGetQuestionnairesAnswersStatus === 200) {
      handleQuestionnaire(useGetQuestionnairesAnswersData?.value)
    }
  }, [useGetQuestionnairesAnswersStatus]);

  const buildValidation = () => {
    const validationSchema = {};

    const questionnaire = handleSaveQuestionnaireAnswer(true);

    for (const question of questionnaire) {
      if (!question.key) continue;
      validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired
        ? yup.string().test("answer-validation", "Campo obrigatório", function (value) {
          return question.answer === 0 ? true : !!question.answer;
        }) : yup.string().notRequired();
    }

    return yup.object().shape(validationSchema);
  }


  const handleQuestionnaire = (data) => {
    let questionnaire = [
      ...data.persons,
      ...data.operations,
      ...data.payments,
      ...data.risks,
      ...data.mrc,
    ];

    const filteredQuestions = questionnaire.filter(qst =>
      qst.idQuestion === 1820
      || qst.idQuestion === 1867
      || qst.idQuestion === 1870
      || qst.idQuestion === 1871
      || qst.idQuestion === 1977

    );

    for (const question of filteredQuestions) {
      if (question.idQuestion === 1820) {
        question.key = 'endorsementReference_1820';
        if (!question?.answer && operationMetadata?.endorsementReference) {
          question.answer = operationMetadata?.endorsementReference;
        }
      }

      if (question.idQuestion === 1867) {
        question.answerOptions.forEach((option) => {
          if (option.idAnswerOption === 7524) {
            option.behaviorObject = {
              isResultDomainDocument: true,
              resultIdDomainDocument: idDomainDocument,
              resultIdDomainStatusDocument: idDomainStatusDocument
            }
          }
        })
      }

      if(question.idQuestion === 1977) {
        question.key = 'premiumCalculation_1977';
      }

      if (endorsmentType === 'off-risk' || endorsmentType === 'policy-extension') {
        if (question.idQuestion === 1870) {
          question.visibleQuestion = false
          question.answer = 'No'
          question.answerJson = 'No'
          question.idAnswerOption = 7529
        }
      }
    }


    setQuestionnaire(filteredQuestions);
  }

  const handleSaveQuestionnaireAnswer = (onlyQuestions) => {
    const body = { questionnaireAnswers: cloneDeep(questionnaire) };
    for (const [key, section] of Object.entries(SECTIONS)) {
      if (section.isMultiple) {
        let multipleFields = []
        switch (section.name) {
          default:
            multipleFields = [];
            break;
        }

        multipleFields.map(group => {
          if (group?.length) {
            const rootQuestion = group.find(question => !question.idQuestionRoot);
            const childrenQuestions = group.filter(question => question.idQuestionRoot);
            rootQuestion.childrenQuestions = childrenQuestions
            if (!onlyQuestions) {
              body.questionnaireAnswers = [...body.questionnaireAnswers, rootQuestion];
            } else {
              body.questionnaireAnswers = [...body.questionnaireAnswers, ...group];
            }
          }
        })
      }
    }

    for (const question of body.questionnaireAnswers) {
      if (
        (
          question.idDomainQuestion === 270 ||
          question.idDomainQuestion === 271 ||
          (question.idDomainQuestion === 44 && typeof question.answer === 'object')
        ) && question.answer
      ) {
        if (!(question.idQuestion === 1977 && typeof question.answer === 'string')) {
          try {
            question.answer = JSON.stringify(question.answer)
          } catch (e) {
          }
        }
      } else if (question.idQuestion === 1820 && question.answer) {
        question.answer = question.answer.padStart(3, '0');
      }
    }

    if (onlyQuestions) return body.questionnaireAnswers;

    useSaveQuestionnaireAnswerRequest(body.questionnaireAnswers);
  }

  const isClient = authContext.enumProfile === 1
    ? true
    : false;

  function handleSummary() {
    useHeaderNbiRequest({ idOperation: props.match.params.idOperation }, null).then(({ data }) => {
      setSummary({
        typeOfCover: data.ret_value.typeOfCover,
        client: data.ret_value.client,
        obligorCoding: data.ret_value.obligorCoding,
        responseBy: data.ret_value.responseBy,
        internalContact: data.ret_value.internalContact,
        broker: data.ret_value.broker,
        dealCode: data.ret_value.deal,
        nmFacility: data.ret_value.nmFacility,
        nmObligor: data.ret_value.nmObligor,
        projectName: data.ret_value.projectName,
        idOperation: props.match.params.idOperation,
      });
      setEndorsmentType(data.ret_value.endorsmentType);
    })
      .catch((error) => {
        mc.error("Erro executando consulta\n" + error);
        console.error('fitPostSummary - Error:', error);
      });
  };

  function handleDocument() {
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestGetDocumentMetadata(props.match.params.idOperation)
      .then(response => response.json())
      .then(response => {
        formik.current?.setFieldValue('policyInception', response?.endorsementData?.policyInception);
        formik.current?.setFieldValue('policyExpire', response?.endorsementData?.policyExpire);
      })
      .catch((error) => {
        mc.error("Error\n" + error);
      });
  }

  function handleGetFiles() {
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestGetDocumentsEndorsementContracts(props.match.params.idOperation)
      .then(data => data.json())
      .then(response => {
        if (response.ret_code !== 200) {
          mc.error(response.ret_msg_details);
          return;
        }
        const files = response.ret_value;
        let aux = [];

        files.forEach(file => {
          let obj = {
            dsTypeDocument: "Attachment",
            nameDocument: file.nameDocument,
            idTypeDocument: file.idTypeDocument,
            nameFile: file.nameFile,
            size: file.size,
            type: file.type,
            urlLink: file.urlLink,
            idDocument: file.idDocument
          };
          aux.push(obj);
        });
        setAttachments(aux);
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleGetFiles.name}\n` + error);
        console.error('fitPostFiles - Error:', error);
      });
  }

  async function generateSignatureEndorsements(showInsuredStamps) {
    loading.start();
    fitinsur.setAuthToken(authContext.token);

    let body = {
      contractChanges: formik.current.values.contractChanges,
      idPreviewDocumentsToSend: idDocumentsToSave,
      insurersToGenerateMrc,
      willGenerateMrcDocument,
      showInsuredStamps: showInsuredStamps == true || showInsuredStamps == false ? showInsuredStamps : formik?.current?.values?.showInsuredStamps
    };

    fitinsur.generateSignatureEndorsements(props.match.params.idOperation, body)
      .then(data => data.json())
      .then(response => {
        if (endorsmentType === 'not-pre-agreed') {
          history.push(`/broking/endorsement-not-pre-agreed/${props.match.params.idOperation}`)
        }


        if (endorsmentType === 'cancellation') {
          history.push(`/broking/endorsement-cancellation/${props.match.params.idOperation}`)
        }

        if (endorsmentType === 'off-risk') {
          history.push(`/broking/endorsement-off-risk/${props.match.params.idOperation}`)
        }

        if (endorsmentType === 'policy-extension') {
          history.push(`/broking/endorsement-policy-extension/${props.match.params.idOperation}`)
        }
        loading.stop();
      }).catch((error) => {
        mc.error(`There was an error while fetching your data: ${generateSignatureEndorsements.name}\n` + error);
      });
  }

  function handleRmvAttach(index) {
    loading.start();
    setAttachments(attachments.filter(tag => attachments.indexOf(tag) !== index));

    const idDocument = attachments[index].idDocument

    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestDeleteSchedulleAssumptionsCorrections(props.match.params.idOperation, idDocument)
      .then(data => data.json())
      .then(resp => {
        loading.stop();

        if (resp.ret_code !== 200) {
          mc.error(resp.ret_msg_details);
          return;
        } else {
          handleGetFiles();
        }
      }).catch(err => {
        mc.error("An error happened, please try again");
        loading.stop();
      });
  };

  async function getOperationInfo() {
    useOperationInfoRequest().then(({ data: response }) => {
      useGetQuestionnairesAnswersRequest();
      setOperationMetadata(response.metadata);
      setIdProduct(response.idProduct);
      setIdDomainDocument(response.idDomainDocument);
      setIdDomainStatusDocument(response.idDomainStatusDocument);
      setOperation(response)
      let documentDomain = null;
      let documentDomainStatus = null;
      if (response.idDomain === 398) {
        documentDomain = 399;
        documentDomainStatus = 118;
      } else if (response.idDomain === 400) {
        documentDomain = 401;
        documentDomainStatus = 143;
      } else if (response.idDomain === 254) {
        documentDomain = 450;
        documentDomainStatus = 180;
      } else if (response.isMarketChanges || response.idDomain === 410) {
        documentDomain = 453;
        documentDomainStatus = 205;
      } else if (response.idDomain === 395) {
        documentDomain = 397;
        documentDomainStatus = 100;
      } else if (response.idDomain === 253) {
        documentDomain = 493;
        documentDomainStatus = 270;
      }
      setIdDomainDocument(documentDomain);
      setIdDomainStatusDocument(documentDomainStatus);
    })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${getOperationInfo.name}\n` + error);
      });
  }

  const validateEndorsementContract = () => {
    const endorsementContractQst = questionnaire.find((question) => question.idQuestion === 1867);
    const { answer } = endorsementContractQst;
    if (answer.length) {
      if (!answer.some((file) => file.documentType.idAnswerOption === '7524')) throw new Error('Please, add the endorsement contract')
    }
  }

  function handleSubmit(values) {
    const premiumCalculationQst = questionnaire.find((qst) => qst.idQuestion === 1977);
    const premiumCalculationAnswer = Array.isArray(premiumCalculationQst?.answer) ? premiumCalculationQst.answer : JSON.parse(premiumCalculationQst?.answer || '[]');
    if (!premiumCalculationAnswer.length && checkMandatoryPremiumCalculation()) {
      mc.error("To process endorsements, you must load a Premium Calculations file.");
      return;
    }

    buildValidation()
      .validate(values, { abortEarly: false })
      .then(() => {
        validateEndorsementContract()
        handleSaveQuestionnaireAnswer()

        const endorsementRefAnswer = questionnaire.find(
          qst => qst.idQuestion === 1820
        )?.answer;

        useUpdateOperationsRequest({
          operationMetadata: {
            ...operationMetadata,
            endorsementReference: endorsementRefAnswer.padStart(3, '0')
          }
        })
      })
      .catch((e) => {
        if (e.message === 'Please, add the endorsement contract') {
          mc.error(e.message)
        } else {
          onValidationError(e, mc, formik, questionnaire, [])
        }

        loading.stop();
      })
  }

  async function generateScheduleReport() {
    return useScheduleReportRequest(null, { finalView: true }).then(({ data }) => {
      if (data.ret_code !== 200) {
        throw new Error('Failed to generate your premium schedule, please try again');
      }
    })
  }

  function checkMandatoryPremiumCalculation() {
    const includeScheduleReportQst = questionnaire.find((qst) => qst.idQuestion === 1870);
    return (includeScheduleReportQst?.answer || '').toUpperCase() === 'YES';
  }

  useEffect(() => {
    if (
      useSaveQuestionnaireAnswerStatus === 200 &&
      useUpdateOperationsStatus === 200
    ) {

      if (checkMandatoryPremiumCalculation()) {
        generateScheduleReport().then((response) => {
          setGenerateSignatureEndorsementsLoading(true);
  
          generateSignatureEndorsements()
        }).catch((error) => {
            console.error('fitPostDocument - Error:', error);
            mc.error('Failed to generate your premium schedule, please try again');
            throw new Error('Failed to generate your premium schedule, please try again');
        });
      } else {
        setGenerateSignatureEndorsementsLoading(true);

        generateSignatureEndorsements();
      }
    } else if (useSaveQuestionnaireAnswerStatus === 400) {
      useSaveQuestionnaireAnswerError.errors?.map(error => {
        if (error.message) mc.error(error.message)
      })
    } else if (useUpdateOperationsStatus === 400) {
      useUpdateOperationsError.errors?.map(error => {
        if (error.message) mc.error(error.message)
      })
    }
  }, [useSaveQuestionnaireAnswerStatus, useUpdateOperationsStatus]);

  const handleEnable = () => {
    fitinsur.setAuthToken(authContext.token);
    fitinsur.wordingAttachments(props.match.params.idOperation)
      .then(x => x.json())
      .then(res => {
        if (res.ret_code != 200) {
          mc.error(res.ret_msg_details);
          return;
        }
      })
  }


  useEffect(() => {
    fitinsur.setAuthToken(authContext.token);

    if (isClient === true) {
      history.push('/list');
      return;
    }

    handleEnable();
    handleSummary();
    handleDocument();
    getOperationInfo();
  }, [])

  const renderTitle = () => {
    if (endorsmentType === 'not-pre-agreed') return 'NOT PRE AGREED - EDIT ENDORSEMENT CONTRACT'
    if (endorsmentType === 'cancellation') return 'CANCELLATION - EDIT ENDORSEMENT CONTRACT'
    if (endorsmentType === 'off-risk') return 'OFF RISK - EDIT ENDORSEMENT CONTRACT'
    if (endorsmentType === 'policy-extension') return 'POLICY EXTENSION ENDORSEMENT'

    return 'ENDORSEMENT DOCUMENT EDIT'
  }

  const validationSchema = yup.object().shape({});

  return (
    <Layout
      idOperation={props.match?.params?.idOperation}
      title={summary.projectName ? summary.projectName : summary.nmObligor ? summary.nmObligor : "Broking name"}
      subtitle={summary.dealCode ? summary.dealCode : "Broking code"}
    >
      <StepNavigation stage={3} idOperation={props.match?.params?.idOperation} />
      <BrokingSummary data={summary} />

      <ContentCard
        title={renderTitle()}
        icon
        info={{
          idOperation: props.match?.params?.idOperation || null,
          idProduct: props.location?.state?.idProduct || null
        }}
      >
        <Formik
          innerRef={formik}
          initialValues={{
            endorsementMessage: "",
          }}
          onSubmit={(values) => handleSubmit(values)}
          validationSchema={validationSchema}
        >
          {({ touched, values, setFieldValue, setFieldError, errors }) => {
            return (
              <Form>
                <FormSection
                  sectionName="ENDORSEMENT_EDIT"
                  setQuestionnaire={setQuestionnaire}
                  values={values}
                  questionnaire={questionnaire}
                  setFieldValue={setFieldValue}
                  formikRef={formik}
                  idOperation={props.match?.params?.idOperation}
                  errors={errors}
                  setFieldError={setFieldError}
                  ignoreModality={true}
                />

                <HasPermission permission={![395].includes(operation?.idDomain)}>
                  <PremiumCalculationSection
                    formikRef={formik}
                    questionnaire={questionnaire}
                    setQuestionnaire={setQuestionnaire}
                    values={values}
                    errors={errors}
                    setFieldValue={setFieldValue}
                    setFieldError={setFieldError}
                    idOperation={props.match.params.idOperation}
                    idOperationParent={props.match.params.idOperationParent}
                    path={props?.path}
                    permissions={props.permissions}
                    useGetQuestionnaireAnswerRequest={useGetQuestionnairesAnswersRequest}
                    useSaveQuestionnaireAnswerRequest={useSaveQuestionnaireAnswerRequest}
                    questionsIdsToSave={[1820, 1867, 1870, 1871, 1977]}
                    onRemove={() => true}
                    hideExportButton={operation?.idDomain === 400 || operation?.idDomain === 398}
                  />
                </HasPermission>


                {endorsmentType === 'policy-extension' &&
                  <Row style={{ marginBottom: '10px' }}>
                    <Col md={3} className="pl-0">
                      <Field
                        name='policyInception'
                        placeholder='Policy Inception'
                        component={InputField}
                        rightIcon='calendar'
                        value={values.policyInception}
                        rightIconClick={() => { setShowCalendarEffective(!showCalendarEffective) }}
                        validation={false}
                        type="text"
                        readOnly />

                      {showCalendarEffective ?
                        <CalendarContainer>
                          <DayPicker
                            style
                            fixedWeeks
                            onDayClick={(date) => {
                              const selectedDate = new Date(date).toLocaleDateString('en-GB');
                              setFieldValue('policyInception', selectedDate);
                              setShowCalendarEffective(false);
                            }}
                          />
                        </CalendarContainer>
                        : null}
                    </Col>

                    <Col md={3} className="pl-0">
                      <Field
                        name='policyExpire'
                        placeholder='Policy Expire'
                        component={InputField}
                        rightIcon='calendar'
                        value={values.policyExpire}
                        rightIconClick={() => { setShowCalendarExpire(!showCalendarExpire) }}
                        validation={false}
                        type="text"
                        readOnly />

                      {showCalendarExpire ?
                        <CalendarContainer>
                          <DayPicker
                            style
                            fixedWeeks
                            onDayClick={(date) => {
                              const selectedDate = new Date(date).toLocaleDateString('en-GB');
                              setFieldValue('policyExpire', selectedDate);
                              setShowCalendarExpire(false);
                            }}
                          />
                        </CalendarContainer>
                        : null}
                    </Col>
                  </Row>
                }

                <ButtonContainer>
                  <Button forth onClick={() => history.goBack()}>&lt; Back</Button>
                  <ButtonContent>
                    {!isApproved && (
                      <>
                        <div style={{ marginTop: "5px" }} >
                          <LostCancelButton idOperation={props.match?.params?.idOperation} />
                        </div>
                        <Button
                          style={{ marginLeft: "10px", marginTop: "5px" }}
                          primary
                          type="submit"
                          id="btnSubmit"
                          disabled={isDisable}
                        >
                          GENERATE PACK
                        </Button>
                      </>
                    )}
                  </ButtonContent>
                </ButtonContainer>
              </Form>
            )
          }}
        </Formik>
      </ContentCard>
      {loading.active && (<Loader backdropColor={Colors.WHITE} fullScreen />)}
    </Layout>
  );
}